/**
 * Resolves natural-language single-email selection within the active browse page (V1.5).
 */

function normalizeAshleyConversationTranscript(transcript) {
  return String(transcript || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const ORDINAL_PATTERNS = Object.freeze([
  { re: /\b(?:number|email number|email|#)\s*(?:one|1)\b|\b(?:the\s+)?first(?:\s+(?:one|email))?\b/, ordinal: 1 },
  { re: /\b(?:number|email number|email|#)\s*(?:two|2)\b|\b(?:the\s+)?second(?:\s+(?:one|email))?\b/, ordinal: 2 },
  { re: /\b(?:number|email number|email|#)\s*(?:three|3)\b|\b(?:the\s+)?third(?:\s+(?:one|email))?\b/, ordinal: 3 },
  { re: /\b(?:number|email number|email|#)\s*(?:four|4)\b|\b(?:the\s+)?fourth(?:\s+(?:one|email))?\b/, ordinal: 4 },
  { re: /\b(?:number|email number|email|#)\s*(?:five|5)\b|\b(?:the\s+)?fifth(?:\s+(?:one|email))?\b/, ordinal: 5 },
  { re: /\b(?:the\s+)?last(?:\s+(?:one|email))?\b/, ordinal: -1 },
]);

const MEMORY_REF_PHRASES = Object.freeze([
  "it",
  "that",
  "that one",
  "this one",
  "that email",
  "this email",
  "the email",
  "read it",
  "read it again",
  "read that",
  "read that one",
  "read that email",
  "tell me about it",
  "tell me about that",
  "tell me about that one",
  "summarize it",
  "summarize it again",
  "summarize that",
  "summarize that again",
  "whats in it",
  "what is in it",
  "open it",
]);

const GO_BACK_PHRASES = Object.freeze([
  "go back",
  "return",
  "back",
  "back to inbox",
  "return to inbox",
  "inbox",
  "continue",
  "back to the list",
  "back to my emails",
]);

const NEWEST_PHRASES = Object.freeze([
  "newest",
  "the newest",
  "newest one",
  "the newest one",
  "latest",
  "the latest",
  "the newer one",
  "newer",
]);

const OLDER_PHRASES = Object.freeze([
  "older",
  "the older",
  "older one",
  "the older one",
  "oldest",
  "the oldest",
  "the earlier one",
  "earlier",
]);

function matchesAny(normalized, phrases) {
  return phrases.some(
    (phrase) => normalized === phrase || normalized.startsWith(`${phrase} `),
  );
}

function senderName(message) {
  return String(message?.fromName || message?.from || "").trim();
}

function normalizeSenderKey(value) {
  return String(value || "")
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Extract a likely sender token from phrases like "read Tim's email".
 * @param {string} normalized
 * @returns {string|null}
 */
export function extractSenderQuery(normalized) {
  const text = String(normalized || "").trim();
  if (!text) {
    return null;
  }

  const patterns = [
    /^(?:read|open|summarize)\s+(.+?)(?:s)?\s+email$/,
    /^tell me about\s+(.+?)(?:s)?\s+email$/,
    /^whats in\s+(.+?)(?:s)?\s+email$/,
    /^what is in\s+(.+?)(?:s)?\s+email$/,
    /^read the email from\s+(.+)$/,
    /^email from\s+(.+)$/,
    /^from\s+(.+)$/,
    /^read\s+(.+?)(?:s)?\s+email$/,
  ];

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match?.[1]) {
      return normalizeSenderKey(match[1]);
    }
  }

  // "read CastleStone" / "CastleStone's email"
  const loose = text.match(
    /^(?:read|tell me about|whats in|what is in)\s+(.+?)(?:\s+email)?$/,
  );
  if (loose?.[1]) {
    const candidate = normalizeSenderKey(loose[1]);
    if (
      candidate &&
      !/^(number|the|first|second|third|fourth|fifth|last|it|that|this|them|those|these)$/.test(
        candidate,
      )
    ) {
      return candidate;
    }
  }

  return null;
}

/**
 * @param {import("../../connectedServices/email/emailProviderTypes").EmailMessageSummary[]} pageMessages
 * @param {string} senderQuery
 */
export function findEmailsBySender(pageMessages, senderQuery) {
  const query = normalizeSenderKey(senderQuery);
  if (!query) {
    return [];
  }
  const list = Array.isArray(pageMessages) ? pageMessages : [];
  return list.filter((message) => {
    const name = normalizeSenderKey(senderName(message));
    if (!name) {
      return false;
    }
    return (
      name === query ||
      name.startsWith(query) ||
      name.includes(query) ||
      query.includes(name.split(" ")[0] || "")
    );
  });
}

/**
 * Resolve newest vs older when Ashley asked for clarification.
 * @param {string} transcript
 * @returns {"newest" | "older" | null}
 */
export function resolveAmbiguousSenderChoice(transcript) {
  const normalized = normalizeAshleyConversationTranscript(transcript);
  if (!normalized) {
    return null;
  }
  if (matchesAny(normalized, NEWEST_PHRASES)) {
    return "newest";
  }
  if (matchesAny(normalized, OLDER_PHRASES)) {
    return "older";
  }
  // Conversational: "the first one" = newest on the page (newest-first list).
  if (/\b(?:the\s+)?first(?:\s+one)?\b/.test(normalized)) {
    return "newest";
  }
  if (/\b(?:the\s+)?second(?:\s+one)?\b/.test(normalized)) {
    return "older";
  }
  return null;
}

/**
 * @param {string} transcript
 * @param {import("../email/emailProviderTypes").EmailMessageSummary[]} pageMessages
 * @returns {{ kind: "ordinal", ordinal: number, message: object, index: number } | { kind: "sender", matches: object[], senderQuery?: string } | { kind: "memory" } | { kind: "go_back" } | null}
 */
export function resolveEmailSelection(transcript, pageMessages) {
  const normalized = normalizeAshleyConversationTranscript(transcript);
  if (!normalized) {
    return null;
  }

  if (matchesAny(normalized, GO_BACK_PHRASES)) {
    return { kind: "go_back" };
  }

  if (matchesAny(normalized, MEMORY_REF_PHRASES)) {
    return { kind: "memory" };
  }

  for (const pattern of ORDINAL_PATTERNS) {
    if (pattern.re.test(normalized)) {
      const page = Array.isArray(pageMessages) ? pageMessages : [];
      if (page.length === 0) {
        return null;
      }
      const ordinal =
        pattern.ordinal === -1 ? page.length : pattern.ordinal;
      if (ordinal < 1 || ordinal > page.length) {
        return null;
      }
      return {
        kind: "ordinal",
        ordinal,
        index: ordinal - 1,
        message: page[ordinal - 1],
      };
    }
  }

  const senderQuery = extractSenderQuery(normalized);
  if (senderQuery) {
    const matches = findEmailsBySender(pageMessages, senderQuery);
    if (matches.length === 0) {
      return { kind: "sender", matches: [], senderQuery };
    }
    return { kind: "sender", matches, senderQuery };
  }

  return null;
}

export function isEmailGoBackPhrase(transcript) {
  const normalized = normalizeAshleyConversationTranscript(transcript);
  return Boolean(normalized && matchesAny(normalized, GO_BACK_PHRASES));
}

export function isEmailMemoryReference(transcript) {
  const normalized = normalizeAshleyConversationTranscript(transcript);
  return Boolean(normalized && matchesAny(normalized, MEMORY_REF_PHRASES));
}

export {
  normalizeAshleyConversationTranscript as normalizeEmailSelectionTranscript,
};
